import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { toast } from "react-toastify";
import SuperAdminSidebar from "./superAdminSidebar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleAPIRequest } from "../../../api/api";
import { setMaintenanceStatus } from "../../../redux/authSlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const Settings = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [termsText,setTermsText] = useState("");
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { maintenanceStatus } = useSelector((state) => state.auth);
  const [mStatus, setMStatus] = useState(maintenanceStatus || "no"); // Default to "no"
  const [formData, setFormData] = useState({
    email1: "",
    email2: "",
    contact1: "",
    contact2: "",
    address1: "",
    address2: "",
  });
  useEffect(() => {
    const fetchFooterInfo = async () => {
      try {
        const response = await handleAPIRequest("get", "get-extra-info",null, null,token);

          setFormData({
            email1: response.email1 || "",
            email2: response.email2 || "",
            contact1: response.contact1 || "",
            contact2: response.contact2 || "",
            address1: response.address1 || "",
            address2: response.address2 || "",
          });
          setTermsText(response.termsText || "");
      } catch (error) {
        console.error("Error fetching footer information:", error);
      }
    };

    fetchFooterInfo();
  }, []);
  const handleSaveFooterInfo = async () => {
    const formDataToSend = new FormData();
    formDataToSend.append("email1", formData.email1);
    formDataToSend.append("email2", formData.email2);
    formDataToSend.append("contact1", formData.contact1);
    formDataToSend.append("contact2", formData.contact2);
    formDataToSend.append("address1", formData.address1);
    formDataToSend.append("address2", formData.address2);

    try {
      const response = await handleAPIRequest("post", "update-footer-info", formDataToSend,null,token);

      if (response) {
        toast.success(response.message);
      } else {
        alert("Failed to update footer information.");
      }
    } catch (error) {
      console.error("Error updating footer information:", error);
      alert("An error occurred while saving the footer information.");
    }
  };

  const handleSaveTermsInfo = async () => {
    const FD = new FormData();
    FD.append("termsText", termsText);
    try {
      const response = await handleAPIRequest("post", "update-terms-info", FD,null,token);

      if (response) {
        toast.success(response.message);
      } else {
        alert("Failed to update terms information.");
      }
    } catch (error) {
      console.error("Error updating terms information:", error);
      toast.error("An error occurred while saving the terms information.");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleMaintenance = async () => {
    try {
      const response = await handleAPIRequest("post", "set-mstatus", null, null, token);
      if (response) {
        const newStatus = response.maintenance_mode;
        setMStatus(newStatus);
        dispatch(setMaintenanceStatus(newStatus)); // Store updated status in Redux

        Swal.fire({
          title: newStatus === "yes" ? "Maintenance Mode Enabled!" : "Maintenance Mode Disabled!",
          icon: "success",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while toggling maintenance mode.",
        icon: "error",
      });
      console.error("API Error:", error);
    }
  };

  const handleMaintenanceToggle = () => {
    Swal.fire({
      title: mStatus === "yes" ? "Disable Maintenance Mode?" : "Enable Maintenance Mode?",
      text: mStatus === "yes"
        ? "This will restore access to all users."
        : "This will log out all users except the Super Admin and restrict access.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: mStatus === "yes" ? "Yes, disable" : "Yes, enable",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        toggleMaintenance();
      }
    });
  };

  const [taxRates, setTaxRates] = useState([]);
  useEffect(() => {
    fetchTaxRates();
  }, []);

  // Fetch tax rates from API
  const fetchTaxRates = async () => {
    try {
      const response = await handleAPIRequest("get", "tax-rates", null, null, token);
      setTaxRates(response || []); // Ensure data exists
    } catch (error) {
      console.error("Error fetching tax rates:", error);
    }
  };

  // Handle tax rate change in state
  const handleTaxRateChange = (id, field, value) => {
    setTaxRates((prevRates) =>
      prevRates.map((rate) =>
        rate.id === id ? { ...rate, [field]: value } : rate
      )
    );
  };

  // Update tax rate via API
  const updateTaxRate = async (id, filerRate, nonFilerRate) => {
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("filer_rate", filerRate);
      formData.append("non_filer_rate", nonFilerRate);
  
      await handleAPIRequest("post", "tax-rates/update", formData, null, token);
  
      Swal.fire({
        title: "Tax Rate Updated!",
        text: "The tax rate has been successfully updated.",
        icon: "success",
      });
  
      fetchTaxRates(); // Refresh data after update
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to update tax rate.",
        icon: "error",
      });
      console.error("API Error:", error);
    }
  };

  // Confirm before updating
  const confirmTaxRateUpdate = (id, filerRate, nonFilerRate) => {
    Swal.fire({
      title: "Update Tax Rate?",
      text: "This will apply the new tax rate.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        updateTaxRate(id, filerRate, nonFilerRate);
      }
    });
  };

  return (
    <div className="flex flex-col md:flex-row">
      <SuperAdminSidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="flex-1 flex flex-col">
        <Navbar toggleSidebar={toggleSidebar} />
        <main className="p-4 md:p-6">
          <ToastContainer />
          <h1 className="text-2xl md:text-3xl font-bold mb-4 text-center md:text-left">
            Settings
          </h1>

          {/* Maintenance Mode */}
          <div className="bg-gray-100 rounded-lg shadow-md p-4 md:p-6 mb-5">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Maintenance Mode
            </h2>
            <p className="mb-4 text-gray-700">
              Enabling maintenance mode will log out all users except the Super Admin and restrict access to the system.
            </p>
            <button
              onClick={handleMaintenanceToggle}
              className={`${
                mStatus === "yes" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
              } text-white font-semibold py-2 px-4 rounded-lg transition duration-300`}
            >
              {mStatus === "yes" ? "Disable Maintenance Mode" : "Enable Maintenance Mode"}
            </button>
          </div>

 {/* Terms And Conditons*/}
 <div className="bg-gray-100 rounded-lg shadow-md p-4 md:p-6 mb-5">
      <h2 className="text-xl md:text-2xl font-semibold mb-4">Terms & Conditions</h2>
      <p className="mb-4 text-gray-700">
       Add the relevant data that is to be shown to the users.
      </p>
      {/* Input Fields */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Content</label>
        <CKEditor
          editor={ClassicEditor}
          data={termsText}
          onChange={(event, editor) => {
            const data = editor.getData();
            setTermsText(data);
          }}
          config={{
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "link",
              "bulletedList",
              "numberedList",
              "blockQuote",
              "|",
              "undo",
              "redo",
            ],
          }}
        />
      </div>

      {/* Save Button */}
      <button
        onClick={handleSaveTermsInfo}
        className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-300"
      >
        Save Data 
      </button>
    </div>

 {/* Footer Information Mode */}
 <div className="bg-gray-100 rounded-lg shadow-md p-4 md:p-6 mb-5">
      <h2 className="text-xl md:text-2xl font-semibold mb-4">Footer Information</h2>
      <p className="mb-4 text-gray-700">
      Add the relevant data that is to be shown to the users.
      </p>

      {/* Input Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="email"
          value={formData.email1}
          onChange={(e) => setFormData({ ...formData, email1: e.target.value })}
          placeholder="Email 1"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="email"
          value={formData.email2}
          onChange={(e) => setFormData({ ...formData, email2: e.target.value })}
          placeholder="Email 2"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={formData.contact1}
          onChange={(e) => setFormData({ ...formData, contact1: e.target.value })}
          placeholder="National Contact Number"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={formData.contact2}
          onChange={(e) => setFormData({ ...formData, contact2: e.target.value })}
          placeholder="International Contact Number"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={formData.address1}
          onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
          placeholder="National Address"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <input
          type="text"
          value={formData.address2}
          onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
          placeholder="International Address"
          className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Save Button */}
      <button
        onClick={handleSaveFooterInfo}
        className="mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-300"
      >
        Save Footer Information
      </button>
    </div>

            {/* Tax Rate Management */}
            <div className="bg-gray-100 rounded-lg shadow-md p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Tax Rate Management</h2>
            <p className="mb-4 text-gray-700">
              Adjust the tax rates for different categories. Changes will be applied immediately.
            </p>

            <div className="space-y-4">
              {taxRates.map((rate) => (
                <div key={rate.id} className="p-4 bg-white rounded-lg shadow">
                  <h3 className="text-lg font-semibold">{rate.category.toUpperCase()}</h3>
                  <div className="flex gap-4 mt-2">
                    <div>
                      <label className="block text-gray-600 text-sm">Filer Rate (%)</label>
                      <input
                        type="number"
                        className="w-full p-2 border rounded"
                        value={rate.filer_rate}
                        onChange={(e) => handleTaxRateChange(rate.id, "filer_rate", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-600 text-sm">Non-Filer Rate (%)</label>
                      <input
                        type="number"
                        className="w-full p-2 border rounded"
                        value={rate.non_filer_rate}
                        onChange={(e) => handleTaxRateChange(rate.id, "non_filer_rate", e.target.value)}
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => confirmTaxRateUpdate(rate.id, rate.filer_rate, rate.non_filer_rate)}
                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-1 px-3 rounded-lg transition duration-300"
                  >
                    Save
                  </button>
                </div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default Settings;
